import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import AdminNavbar from 'components/Navbars/AdminNavbar'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import categoryValidator from 'validators/Admin/categoryValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'

import {
  Container,
  Form,
  FormField,
  Title,
  Input,
  Row,
  InfoText,
  WarningText,
  ErrorContainer
} from './styles'

export default function EditServiceCategory () {
  const [loading, setLoading] = useState (true)
  const [category, setCategory] = useState (null)
  const [errorMessage, setErrorMessage] = useState (null)
  const [showDeleteDialog, setShowDeleteDialog] = useState (false)
  const [showRestoreDialog, setShowRestoreDialog] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const token = AuthService.getToken ()
  const { id: category_id } = useParams ()
  const navigate = useNavigate ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [])

  const getData = async () => {
    try {
      const { data: category } = await cutileiApi.get (
        `/service_categories/${category_id}`,
        requestConfig
      )

      setCategory (category)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const deleteCategory = async () => {
    try {
      await cutileiApi.delete (`/service_categories/${category_id}`, requestConfig)
      navigate ('/admin/config', { replace: true })
    } catch (error) {
      logApiError (error)
    }
  }

  const restoreCategory = async () => {
    try {
      await cutileiApi.post (`/service_categories/${category_id}/reactivate`, {}, requestConfig)
      navigate ('/admin/config', { replace: true })
    } catch (error) {
      logApiError (error)
    }
  }

  const toggleDeleteDialog = () => setShowDeleteDialog (!showDeleteDialog)
  const toggleRestoreDialog = () => setShowRestoreDialog (!showRestoreDialog)
  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  return (
    <Container>
      <AdminNavbar/>
      {loading ? <Loading/> : (
        <Formik
          validationSchema={categoryValidator}
          initialValues={{
            'name': category.name,
            'description': category.description ?? ''
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await cutileiApi.put (`/service_categories/${category_id}`, values, requestConfig)

              setSubmitting (false)
              toggleOkDialog ()
            } catch (error) {
              setSubmitting (false)
              logApiError (error)
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleBlur,
            handleSubmit
          }) => (
            <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
              <Title>Editar categoria de serviço</Title>
              <Row>
                <FormField>
                  <Input 
                    placeholder='Nome'
                    value={values.name}
                    onChange={handleChange ('name')} 
                    onBlur={handleBlur ('name')}
                  />
                  {touched.name && errors.name && (
                    <WarningText>
                      {errors.name}
                    </WarningText>
                  )}
                </FormField>
              </Row>
              <Row>
                <FormField>
                  <Input 
                    placeholder='Descrição'
                    value={values.description}
                    onChange={handleChange ('description')} 
                    onBlur={handleBlur ('description')}
                  />
                  {touched.description && errors.description && (
                    <WarningText>
                      {errors.description}
                    </WarningText>
                  )}
                </FormField>
              </Row>
              {errorMessage && (
                <ErrorContainer>
                  <InfoText>{errorMessage}</InfoText>
                </ErrorContainer>
              )}
              <MainButton
                label='Salvar alterações'
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                width={180}
                style={{marginBottom: 10}}
              />
              {category.deleted ? (
                <SecondaryButton
                  label='Reativar categoria'
                  onClick={toggleRestoreDialog}
                  width={180}
                />
              ) : (
                <MainButton
                  label='Excluir categoria'
                  onClick={toggleDeleteDialog}
                  dangerous={true}
                  width={180}
                />
              )}
            </Form>
          )}
        </Formik>
      )}
      <AlertDialog
        visible={showDeleteDialog}
        title='Atenção!'
        message='Deseja realmente excluir esta categoria?'
        confirmText='Sim'
        cancelText='Não'
        onConfirm={deleteCategory}
        onClose={toggleDeleteDialog}
        dangerous
      />
      <AlertDialog
        visible={showRestoreDialog}
        title='Atenção!'
        message='Deseja realmente reativar esta categoria?'
        confirmText='Sim'
        cancelText='Não'
        onConfirm={restoreCategory}
        onClose={toggleRestoreDialog}
      />
      <AlertDialog
        visible={showOkDialog}
        title='Sucesso'
        message='Alterações salvas!'
        confirmText='Ok'
        onConfirm={() => navigate ('/admin/config', { replace: true })}
        onClose={() => navigate ('/admin/config', { replace: true })}
      />
    </Container>
  )
}
